import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageRoutes } from "../constants/routes";
import ConsoleIcon from "../components/common/NavigationBar/assets/console_1x.webp";
import ConsoleIcon2x from "../components/common/NavigationBar/assets/console_2x.webp";
import ConsoleIcon3x from "../components/common/NavigationBar/assets/console_3x.webp";
import libraryIcon from "../components/common/NavigationBar/assets/library_1x.webp";
import libraryIcon2x from "../components/common/NavigationBar/assets/library_2x.webp";
import libraryIcon3x from "../components/common/NavigationBar/assets/library_3x.webp";
import ownGameIcon from "../components/common/NavigationBar/assets/ownGame_1x.webp";
import ownGameIcon2x from "../components/common/NavigationBar/assets/ownGame_2x.webp";
import ownGameIcon3x from "../components/common/NavigationBar/assets/ownGame_3x.webp";
import settingsIcon from "../components/common/NavigationBar/assets/settings_1x.webp";
import settingsIcon2x from "../components/common/NavigationBar/assets/settings_2x.webp";
import settingsIcon3x from "../components/common/NavigationBar/assets/settings_3x.webp";
import SearchIcon from "../assets/icons/search.webp";
import SearchIcon2x from "../assets/icons/search@2x.webp";
import SearchIcon3x from "../assets/icons/search@3x.webp";
import {
  Game,
  GameSession,
  gameQueueStatusGQL,
  lastActiveGameSessionGQL,
  Query,
  recentlyPlayedGamesAaaGQL,
} from "@onmo/graphql/graphql";
import { useQuery } from "@apollo/client";
import { NavItem } from "../types/navigationBar";
import { NavigationMd } from "../models/navigation";
import { useAppSelector } from "../components/hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import { useIsKidProfile } from "./useProfileSession";

type NavigationBarItems = NavItem[] & { loading: boolean };

function makeGameNavItem(
  navigate: ReturnType<typeof useNavigate>,
  game: GameSession | Game,
): NavItem {
  const { src1x, src2x, src3x } = NavigationMd.getContent(game);
  const isGameSession = NavigationMd.isGameSession(game);

  return {
    id: `game-${isGameSession ? game?.game.id : game?.id}`,
    image: {
      src1x: src1x,
      src2x: src2x,
      src3x: src3x,
    },
    icon: "",
    path: `${PageRoutes.Game}/${isGameSession ? game?.game.id : game?.id}`,
    nameKey: "",
    onAction: () =>
      navigate(
        `${PageRoutes.Game}/${isGameSession ? game?.game.id : game?.id}`,
      ),
    game: game,
  };
}

export function useNavigationBar(gameOnly = false) {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isKid = useIsKidProfile();
  const { data: dataRecentlyPlayed, loading: loadingDataRecentPlayed } =
    useQuery<Query>(recentlyPlayedGamesAaaGQL, {
      skip: !isAuthenticated,
      variables: { input: { page: { limit: 20 } } },
    });
  const {
    data: dataLastActive,
    loading: loadingDataLastActive,
    startPolling,
    stopPolling,
  } = useQuery<Query>(lastActiveGameSessionGQL, {
    skip: !isAuthenticated,
  });
  const { data: dataQueue } = useQuery<Query>(gameQueueStatusGQL, {
    skip: !isAuthenticated,
  });
  const lastActiveSession: GameSession | undefined =
    (dataLastActive?.me?.lastActiveGameSession as GameSession | undefined) ??
    undefined;

  // Refetch last active session after 10 seconds if exists
  useEffect(() => {
    if (lastActiveSession) {
      startPolling(10_000); // poll every 10s
    } else stopPolling();
    return () => stopPolling();
  }, [lastActiveSession, startPolling, stopPolling]);

  const head: NavItem[] = useMemo(
    () =>
      [
        {
          id: "console",
          image: {
            src1x: ConsoleIcon,
            src2x: ConsoleIcon2x,
            src3x: ConsoleIcon3x,
          },
          icon: undefined,
          path: PageRoutes.Home,
          nameKey: "Navigation_Console",
          onAction: () =>
            navigate(PageRoutes.Home, { state: { restoreScroll: true } }),
        },
        {
          id: "essential-pass",
          image: {
            src1x: libraryIcon,
            src2x: libraryIcon2x,
            src3x: libraryIcon3x,
          },
          path: PageRoutes.EssentialPass,
          nameKey: "Navigation_Essential Pass",
          onAction: () =>
            navigate(PageRoutes.EssentialPass, {
              state: { restoreScroll: true },
            }),
        },
        {
          id: "install-your-games",
          image: {
            src1x: ownGameIcon,
            src2x: ownGameIcon2x,
            src3x: ownGameIcon3x,
          },
          icon: undefined,
          path: PageRoutes.InstallGame,
          nameKey: "Navigation_Install & Play",
          onAction: () =>
            navigate(PageRoutes.InstallGame, {
              state: { restoreScroll: true },
            }),
        },
      ].filter((item) => !isKid || item.path !== PageRoutes.InstallGame),
    [navigate, isKid],
  );

  const recentlyPlayed: NavItem[] = useMemo(() => {
    if (dataRecentlyPlayed) {
      sessionStorage.setItem(
        "prev-recents-played",
        JSON.stringify(dataRecentlyPlayed?.me?.recentlyPlayedGames),
      );
    }
    const prevRecentlyPlayed = JSON.parse(
      sessionStorage.getItem("prev-recents-played") || "[]",
    ) as Game[];

    const items: NavItem[] = [];
    const recents: Game[] = dataRecentlyPlayed
      ? dataRecentlyPlayed?.me?.recentlyPlayedGames
      : prevRecentlyPlayed;

    // The queued game is available only when gameQueueStatus returns a game
    // (null when the user is not queued or the queued game is unavailable).
    const queuedGame: Game | undefined =
      dataQueue?.gameQueueStatus?.game ?? undefined;

    if (queuedGame) {
      items.push(makeGameNavItem(navigate, queuedGame));
    }
    if (lastActiveSession && lastActiveSession.game.id !== queuedGame?.id) {
      items.push(makeGameNavItem(navigate, lastActiveSession));
    }
    if (recents) {
      for (const g of recents) {
        if (lastActiveSession?.game.id !== g.id && queuedGame?.id !== g.id) {
          items.push(makeGameNavItem(navigate, g));
        }
      }
    }

    return items.slice(0, gameOnly ? 20 : 5);
  }, [dataRecentlyPlayed, dataQueue, gameOnly, lastActiveSession, navigate]);

  const tail: NavItem[] = useMemo(
    () => [
      {
        id: "icon-search",
        image: {
          src1x: SearchIcon,
          src2x: SearchIcon2x,
          src3x: SearchIcon3x,
        },
        icon: "icon-search",
        path: PageRoutes.Search,
        nameKey: "",
        onAction: () => navigate(PageRoutes.Search),
      },
      {
        id: "icon-setting",
        image: {
          src1x: settingsIcon,
          src2x: settingsIcon2x,
          src3x: settingsIcon3x,
        },
        icon: "icon-setting",
        path: PageRoutes.Settings,
        nameKey: "",
        onAction: () => navigate(PageRoutes.Settings),
      },
    ],
    [navigate],
  );

  const getItems = (): NavItem[] => {
    if (!isAuthenticated) return [...head, ...recentlyPlayed];
    if (gameOnly) return recentlyPlayed;
    return [...head, ...recentlyPlayed, ...tail];
  };

  const items = getItems();

  const recentlyPlayedGames = items as NavigationBarItems;
  recentlyPlayedGames.loading =
    isAuthenticated && (loadingDataRecentPlayed || loadingDataLastActive);
  return recentlyPlayedGames;
}
